import { useContext } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AuthContext } from '@@/contexts/AuthContext';
import { InsightsApiClient, GetWisdomSpansPayload, GetWisdomSpansResponse } from '../../api/api-client';
import { ResultValue } from '@@/types/ApiResult';

export const useWisdomSpans = (parameters: GetWisdomSpansPayload) => {
  const { instanceData } = useContext(AuthContext);

  const fetchSpans = async () => {
    if (!instanceData?.instanceId) {
      throw new Error('Instance data is not available');
    }

    const client = new InsightsApiClient();
    const result = await client.getWisdomSpans({
      instanceId: instanceData.instanceId,
      region: instanceData.region,
      assistantId: parameters.assistantId,
      sessionId: parameters.sessionId
    });

    if (!ResultValue.ok(result)) {
      throw new Error(result?.message || 'Failed to fetch wisdom spans');
    }

    return result.data;
  };

  const { data, isLoading, error, refetch, isRefetching } = useQuery<GetWisdomSpansResponse, Error>({
    queryKey: ['wisdom-spans', instanceData?.instanceId, instanceData?.region, parameters],
    queryFn: fetchSpans,
    enabled: !!(instanceData?.instanceId && instanceData?.region && parameters.assistantId && parameters.sessionId),
    refetchOnWindowFocus: false,
    staleTime: 0,
    retry: 0
  });

  return { data, isLoading, isRefetching, error, refetch };
};
